import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {Camera, Images, RotateCcw, X, Zap} from 'lucide-react';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';
import {EyebrowPill} from '../components/EyebrowPill';
import {InputModeToggle} from '../components/app/InputModeToggle';
import {SourceButton} from '../components/app/SourceButton';
import {PhoneFrame} from '../components/app/PhoneFrame';

export type SnapToTimerProps = {
  eyebrow: string;
  whiteboardText: string;
  parsedTitle: string;
  workoutType: string;
  totalRounds: number;
  totalRests: number;
  totalSecondsLabel: string;
};

// Photo-only flow, 12s = 360 frames @ 30fps
const PHOTO_BUTTONS_IN = 30;
const PHOTO_CURSOR_MOVE = 85;
const PHOTO_TAP = 135;
const CAMERA_FADE_IN_START = 145;
const CAMERA_FADE_IN_END = 165;
const SHUTTER_TAP = 220;
const FLASH_END = 230;
const CAMERA_FADE_OUT_START = 245;
const CAMERA_FADE_OUT_END = 265;
const PHOTO_SPINNER_START = 260;
const PHOTO_DIAL_FADE_OUT = 305;
const PHOTO_DIAL_FADE_IN = 315;

export const SnapToTimer: React.FC<SnapToTimerProps> = ({
  eyebrow,
  whiteboardText,
  parsedTitle,
  workoutType,
  totalRounds,
  totalRests,
  totalSecondsLabel,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const headerOpacity = interpolate(frame, [0, 14], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const phoneSpring = spring({
    frame: Math.max(0, frame - 4),
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 90},
  });
  const phoneY = interpolate(phoneSpring, [0, 1], [60, 0]);
  const phoneOpacity = interpolate(frame, [4, 22], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill>
      <BgGradient />
      <Audio src={staticFile('music/photo-to-timer.mp3')} volume={0.4} />

      <AbsoluteFill
        style={{
          padding: '90px 60px 110px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 30,
        }}
      >
        <div style={{opacity: headerOpacity}}>
          <EyebrowPill label={eyebrow} />
        </div>

        <div
          style={{
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: phoneOpacity,
            transform: `translateY(${phoneY}px)`,
          }}
        >
          <PhoneFrame width={620} height={1280}>
            <PhoneInterior
              frame={frame}
              fps={fps}
              whiteboardText={whiteboardText}
              parsedTitle={parsedTitle}
              workoutType={workoutType}
              totalRounds={totalRounds}
              totalRests={totalRests}
              totalSecondsLabel={totalSecondsLabel}
            />
          </PhoneFrame>
        </div>

        <Caption frame={frame} />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const PhoneInterior: React.FC<{
  frame: number;
  fps: number;
  whiteboardText: string;
  parsedTitle: string;
  workoutType: string;
  totalRounds: number;
  totalRests: number;
  totalSecondsLabel: string;
}> = ({frame, fps, whiteboardText, parsedTitle, workoutType, totalRounds, totalRests, totalSecondsLabel}) => {
  // Dial fade-in
  const dialOpacity = interpolate(
    frame,
    [PHOTO_DIAL_FADE_IN, PHOTO_DIAL_FADE_IN + 14],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  // Dial scale-up spring (drama on reveal)
  const dialScaleSpring = spring({
    frame: Math.max(0, frame - PHOTO_DIAL_FADE_IN),
    fps,
    from: 0.86,
    to: 1,
    config: {damping: 12, stiffness: 90},
  });
  const dialVisible = frame >= PHOTO_DIAL_FADE_IN - 4;

  // Input pane fades out as dial reveals
  const inputBaseOpacity = (() => {
    const fadeOut = interpolate(
      frame,
      [PHOTO_DIAL_FADE_OUT, PHOTO_DIAL_FADE_IN],
      [1, 0],
      {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
    );
    if (frame < CAMERA_FADE_IN_START) return fadeOut;
    if (frame < CAMERA_FADE_IN_END)
      return interpolate(frame, [CAMERA_FADE_IN_START, CAMERA_FADE_IN_END], [1, 0]);
    if (frame < CAMERA_FADE_OUT_START) return 0;
    if (frame < CAMERA_FADE_OUT_END)
      return interpolate(frame, [CAMERA_FADE_OUT_START, CAMERA_FADE_OUT_END], [0, 1]);
    return fadeOut;
  })();

  // Camera scene opacity (full-bleed over the input pane)
  const cameraOpacity = (() => {
    if (frame < CAMERA_FADE_IN_START) return 0;
    if (frame < CAMERA_FADE_IN_END)
      return interpolate(frame, [CAMERA_FADE_IN_START, CAMERA_FADE_IN_END], [0, 1]);
    if (frame >= CAMERA_FADE_OUT_END) return 0;
    if (frame >= CAMERA_FADE_OUT_START)
      return interpolate(frame, [CAMERA_FADE_OUT_START, CAMERA_FADE_OUT_END], [1, 0]);
    return 1;
  })();

  return (
    <div style={{position: 'absolute', inset: 0}}>
      {/* Photo-mode input pane */}
      {frame < PHOTO_DIAL_FADE_IN ? (
        <Act1PhotoPane frame={frame} fps={fps} opacity={inputBaseOpacity} />
      ) : null}

      {/* Camera scene (opens after Take photo tap) */}
      {cameraOpacity > 0 ? (
        <CameraScene
          opacity={cameraOpacity}
          frame={frame}
          parsedTitle={parsedTitle}
          whiteboardText={whiteboardText}
        />
      ) : null}

      {/* Final timer dial reveal */}
      {dialVisible ? (
        <DialPane
          opacity={dialOpacity}
          scale={dialScaleSpring}
          parsedTitle={parsedTitle}
          workoutType={workoutType}
          totalRounds={totalRounds}
          totalRests={totalRests}
          totalSecondsLabel={totalSecondsLabel}
        />
      ) : null}
    </div>
  );
};

const Act1PhotoPane: React.FC<{frame: number; fps: number; opacity: number}> = ({
  frame,
  fps,
  opacity,
}) => {
  const buttonsSpring = spring({
    frame: Math.max(0, frame - PHOTO_BUTTONS_IN),
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 100},
  });
  const buttonsOpacity = interpolate(frame, [PHOTO_BUTTONS_IN, PHOTO_BUTTONS_IN + 16], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const buttonsY = interpolate(buttonsSpring, [0, 1], [30, 0]);

  const isLoading = frame >= PHOTO_SPINNER_START && frame < PHOTO_DIAL_FADE_OUT;

  // Cursor moves to "Take photo" then a tap pulse at PHOTO_TAP.
  // With expanded pacing, the movement spans ~50 frames (1.67s) — slow and deliberate.
  const cursorX = interpolate(
    frame,
    [PHOTO_CURSOR_MOVE, PHOTO_TAP - 5],
    [400, 230],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const cursorY = interpolate(
    frame,
    [PHOTO_CURSOR_MOVE, PHOTO_TAP - 5],
    [560, 380],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const cursorOpacity = interpolate(
    frame,
    [PHOTO_CURSOR_MOVE - 6, PHOTO_CURSOR_MOVE, CAMERA_FADE_IN_START - 2, CAMERA_FADE_IN_START + 6],
    [0, 1, 1, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const tapPulse = interpolate(
    frame,
    [PHOTO_TAP - 4, PHOTO_TAP, PHOTO_TAP + 10],
    [0, 1.7, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        padding: '110px 28px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
        opacity,
      }}
    >
      <InputModeToggle selected="photo" />

      <div
        style={{
          opacity: buttonsOpacity,
          transform: `translateY(${buttonsY}px)`,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <SourceButton
          icon={<Camera size={28} color={brand.primary} strokeWidth={2.2} />}
          title="Take photo"
          subtitle="Use the camera for a new whiteboard photo."
          isLoading={isLoading}
          frame={frame}
        />
        <SourceButton
          icon={<Images size={28} color={brand.primary} strokeWidth={2.2} />}
          title="Choose from gallery"
          subtitle="Import an existing image or screenshot."
        />
      </div>

      <TouchCursor x={cursorX} y={cursorY} opacity={cursorOpacity} pulse={tapPulse} />
    </div>
  );
};

const TouchCursor: React.FC<{x: number; y: number; opacity: number; pulse: number}> = ({
  x,
  y,
  opacity,
  pulse,
}) => (
  <div
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: 44,
      height: 44,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.92)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
      opacity,
      pointerEvents: 'none',
      transform: 'translate(-50%, -50%)',
    }}
  >
    {pulse > 0 ? (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: `3px solid ${brand.primary}`,
          opacity: 1 - pulse / 1.7,
          transform: `scale(${pulse})`,
        }}
      />
    ) : null}
  </div>
);

const DialPane: React.FC<{
  opacity: number;
  scale: number;
  parsedTitle: string;
  workoutType: string;
  totalRounds: number;
  totalRests: number;
  totalSecondsLabel: string;
}> = ({opacity, scale}) => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      opacity,
      transform: `scale(${scale})`,
      transformOrigin: 'center',
    }}
  >
    <Img
      src={staticFile('images/app-timer-custom-ready.png')}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
      }}
    />
  </div>
);

const Caption: React.FC<{frame: number}> = ({frame}) => {
  const opacity = interpolate(frame, [30, 54], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const label = (() => {
    if (frame < PHOTO_TAP) return 'Snap your whiteboard.';
    if (frame < PHOTO_SPINNER_START) return ' ';
    if (frame < PHOTO_DIAL_FADE_IN) return 'AI reads it.';
    return 'Timer ready.';
  })();

  return (
    <div
      style={{
        opacity,
        fontFamily: brandFont,
        fontSize: 40,
        fontWeight: 700,
        color: brand.textPrimary,
        letterSpacing: -0.4,
        textAlign: 'center',
        minHeight: 56,
      }}
    >
      {label}
    </div>
  );
};

const CameraScene: React.FC<{
  opacity: number;
  frame: number;
  parsedTitle: string;
  whiteboardText: string;
}> = ({opacity, frame, parsedTitle, whiteboardText}) => {
  // Whiteboard zoom-in to feel like camera focusing
  const focusProgress = interpolate(
    frame,
    [CAMERA_FADE_IN_END, SHUTTER_TAP - 6],
    [1.04, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  // Shutter pulse + capture freeze
  const shutterPulse = interpolate(
    frame,
    [SHUTTER_TAP - 4, SHUTTER_TAP, SHUTTER_TAP + 8],
    [0, 1.5, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const flashOpacity = interpolate(
    frame,
    [SHUTTER_TAP, SHUTTER_TAP + 3, FLASH_END],
    [0, 0.92, 0],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const captureFreeze = interpolate(
    frame,
    [SHUTTER_TAP, SHUTTER_TAP + 6],
    [1, 0.97],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  // Whiteboard text — strip "For time:" header to mimic raw board content
  const boardLines = whiteboardText
    .split('\n')
    .filter((l) => l.trim() && !l.toLowerCase().includes('for time'));

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        opacity,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '52px 28px 0',
          color: 'rgba(255,255,255,0.92)',
          fontFamily: brandFont,
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} color="rgba(255,255,255,0.92)" strokeWidth={2.4} />
          </div>
        </div>
        <div style={{letterSpacing: 1, textTransform: 'uppercase', fontSize: 14, opacity: 0.7}}>
          Photo
        </div>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Zap size={16} color="rgba(255,255,255,0.85)" strokeWidth={2.4} />
        </div>
      </div>

      {/* Viewfinder area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {/* Subtle grid lines for camera viewfinder feel */}
        <div
          style={{
            position: 'absolute',
            inset: 60,
            border: '1px solid rgba(255,255,255,0.08)',
            pointerEvents: 'none',
          }}
        />
        <Whiteboard
          title={parsedTitle}
          lines={boardLines}
          scale={focusProgress * captureFreeze}
        />
      </div>

      {/* Bottom controls — gallery / shutter / flip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 36px 60px',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            background: 'rgba(255,255,255,0.16)',
            border: '1.5px solid rgba(255,255,255,0.25)',
          }}
        />
        <ShutterButton pulse={shutterPulse} />
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RotateCcw size={20} color="rgba(255,255,255,0.78)" strokeWidth={2.2} />
        </div>
      </div>

      {/* Capture flash */}
      {flashOpacity > 0 ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,1)',
            opacity: flashOpacity,
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </div>
  );
};

const Whiteboard: React.FC<{title: string; lines: string[]; scale: number}> = ({
  title,
  lines,
  scale,
}) => (
  <div
    style={{
      transform: `scale(${scale})`,
      width: '78%',
      aspectRatio: '4 / 3',
      background: '#F5EAC9',
      borderRadius: 6,
      padding: '28px 32px',
      boxShadow:
        '0 18px 50px rgba(0,0,0,0.55), inset 0 0 0 6px #C8AE6B, inset 0 0 30px rgba(0,0,0,0.06)',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      fontFamily: brandFont,
      color: '#2A1B0E',
      letterSpacing: -0.5,
    }}
  >
    <div
      style={{
        fontSize: 44,
        fontWeight: 900,
        letterSpacing: 4,
        textTransform: 'uppercase',
        marginBottom: 4,
      }}
    >
      {title}
    </div>
    {lines.map((line, i) => (
      <div
        key={i}
        style={{
          fontSize: 26,
          fontWeight: 700,
          opacity: 0.88,
        }}
      >
        {line}
      </div>
    ))}
  </div>
);

const ShutterButton: React.FC<{pulse: number}> = ({pulse}) => (
  <div
    style={{
      width: 92,
      height: 92,
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.16)',
      border: '4px solid rgba(255,255,255,0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    }}
  >
    <div
      style={{
        width: 70,
        height: 70,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.95)',
      }}
    />
    {pulse > 0 ? (
      <div
        style={{
          position: 'absolute',
          inset: -6,
          borderRadius: '50%',
          border: `3px solid ${brand.primary}`,
          opacity: 1 - pulse / 1.5,
          transform: `scale(${pulse})`,
        }}
      />
    ) : null}
  </div>
);
